/**
 * 错误处理工具
 * 基于 product/protocol/error-codes.md
 */

import type { ErrorResponse } from '@/shared/types/api'

/**
 * 错误码到友好提示的映射
 */
const ERROR_MESSAGES: Record<string, string> = {
  // 认证相关
  AUTH_001: '邮箱已被注册',
  AUTH_002: '密码强度不足',
  AUTH_003: '邮箱格式不正确',
  AUTH_004: '邮箱或密码错误',
  AUTH_005: '账号已被禁用，请联系管理员',
  AUTH_006: '登录尝试过多，请稍后再试',
  AUTH_007: '登录已过期，请重新登录',
  AUTH_008: '登录无效，请重新登录',
  AUTH_009: '验证码已过期，请重新获取',
  AUTH_010: '验证码错误，请检查后重试',
  AUTH_011: '验证码发送过于频繁，请60秒后再试',
  AUTH_012: '邮件发送失败，请稍后重试',
  
  // 用户相关
  USER_001: '原密码错误',
  USER_002: '新密码强度不足',
  USER_003: '新密码不能与原密码相同',
  USER_004: '邮箱格式不正确',
  USER_005: '姓名长度不符合要求',
  USER_006: '头像URL格式不正确',
  USER_007: '用户不存在',
  USER_008: '无权查看该用户信息',
  USER_009: '无权修改该用户信息',
  USER_010: '不能删除自己',
  USER_011: '用户还有关联的项目，请先转移',
  
  // 项目相关
  PROJ_002: '已达到项目数量上限',
  PROJ_003: '项目不存在',
  PROJ_004: '无权访问该项目',
  PROJ_005: '无权编辑该项目',
  PROJ_006: '项目名称已被使用',
  PROJ_007: '只有项目所有者可以删除项目',
  PROJ_008: '项目名称确认不匹配',
  PROJ_009: '项目正在运行中，请先停止',
  PROJ_010: '用户不存在',
  PROJ_011: '用户已是项目成员',
  PROJ_012: '无权添加成员',
  PROJ_013: '不能移除项目所有者',
  
  // 流程相关
  FLOW_001: '流程配置无效',
  FLOW_002: '流程配置不存在',
  FLOW_003: '无权访问该流程配置',
  FLOW_004: '无权编辑该流程配置',
  FLOW_005: '流程配置保存失败',
  FLOW_006: '版本冲突，请刷新后重试',
  FLOW_007: '指定版本不存在',
  FLOW_008: '流程发布失败',
  FLOW_009: '流程回滚失败',
  
  // 对话相关
  CHAT_001: '会话不存在',
  CHAT_002: '消息内容不能为空',
  CHAT_003: '消息过长',
  CHAT_004: '项目维护中，暂时无法使用',
  CHAT_005: '请求超时，请稍后重试',
  CHAT_006: 'AI服务调用失败，请稍后重试',
  CHAT_007: '会话已过期',
  
  // API Key相关
  API_001: 'API Key 无效',
  API_002: 'API Key 已过期',
  API_003: 'API Key 已被禁用',
  API_004: 'API Key 使用频率超限',
  
  // 限流相关
  RATE_001: '请求过于频繁，请稍后再试',
  
  // 权限相关
  PERM_001: '权限不足',
  PERM_002: '需要管理员权限',
  PERM_003: '需要项目所有者权限',
  
  // 系统相关
  SYS_001: '服务器内部错误',
  SYS_002: '服务暂时不可用',
  SYS_003: '服务维护中',
  SYS_004: '数据库连接失败',
  SYS_005: 'Redis连接失败',
  SYS_006: 'DynamoDB操作失败',
  
  // LLM相关
  LLM_001: 'AI服务调用失败',
  LLM_002: 'AI服务密钥无效',
  LLM_003: 'AI服务请求超时',
  LLM_004: '不支持的模型',
  LLM_005: '模型参数无效',
  
  // 工具相关
  TOOL_001: '工具不存在',
  TOOL_002: '工具正在使用中，无法删除',
}

/**
 * 获取错误友好提示
 */
export function getErrorMessage(error: ErrorResponse['error']): string {
  // 添加空值检查，防止解构失败
  if (!error) {
    return '操作失败，请稍后重试'
  }
  
  const { code, message } = error
  
  // 优先使用后端返回的具体错误消息（422错误会包含详细的验证信息）
  if (message) {
    return message
  }
  
  // 其次使用错误码映射的友好提示
  if (code && ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code]
  }
  
  // 默认提示
  return '操作失败，请稍后重试'
}

/**
 * 判断错误是否需要特殊处理
 */
export function shouldHandleError(error: ErrorResponse['error']): {
  shouldRetry: boolean
  shouldRedirect: boolean
  redirectTo?: string
} {
  // 添加空值检查
  if (!error) {
    return {
      shouldRetry: false,
      shouldRedirect: false,
    }
  }
  
  const { code } = error
  
  // 需要重新登录的错误
  if (code === 'AUTH_007' || code === 'AUTH_008') {
    return {
      shouldRetry: false,
      shouldRedirect: true,
      redirectTo: '/login',
    }
  }
  
  // 权限不足的错误
  if (code?.startsWith('PERM_') || code === 'PROJ_004' || code === 'PROJ_005') {
    return {
      shouldRetry: false,
      shouldRedirect: false,
    }
  }
  
  // 网络错误可以重试
  if (code?.startsWith('SYS_') || code === 'CHAT_005' || code === 'LLM_003') {
    return {
      shouldRetry: true,
      shouldRedirect: false,
    }
  }
  
  return {
    shouldRetry: false,
    shouldRedirect: false,
  }
}

/**
 * 记录错误日志
 */
export function logError(error: ErrorResponse['error'], context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.error('API Error:', {
      code: error?.code,
      message: error?.message,
      details: error?.details,
      context,
    })
  }
  
  // 生产环境可以接入 Sentry 等错误追踪服务
  // if (import.meta.env.PROD) {
  //   Sentry.captureException(new Error(error.message), {
  //     tags: { errorCode: error.code },
  //     extra: { ...error.details, ...context },
  //   })
  // }
}

